import { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { api } from "../lib/api";

/**
 * Lifestyle scene picker — full-screen modal.
 *
 * Loads the room→scenes catalogue once and lets the realtor pick a
 * pre-baked scene OR write a free-text "custom subjects" override
 * (e.g. "an elderly couple having tea at the table by the window").
 */
export default function LifestylePicker({ onClose, onGo }) {
  const [catalogue, setCatalogue] = useState(null);
  const [activeRoom, setActiveRoom] = useState(null);
  const [activeScene, setActiveScene] = useState(null);
  const [custom, setCustom] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/lifestyle/catalogue")
      .then(({ data }) => {
        setCatalogue(data);
        if (data?.rooms?.length) {
          setActiveRoom(data.rooms[0].id);
          setActiveScene(data.rooms[0].scenes[0]?.id || null);
        }
      })
      .catch(() => {});
  }, []);

  const rooms = catalogue?.rooms || [];
  const scenesForRoom =
    rooms.find((r) => r.id === activeRoom)?.scenes || [];

  const handleGo = () => {
    if (!activeScene && !custom.trim()) return;
    setBusy(true);
    // Defer to the parent — it spawns the actual API call + toast.
    onGo({
      scene_id: custom.trim() ? null : activeScene,
      custom_subjects: custom.trim() || null,
    });
  };

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/85 backdrop-blur-md flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-[#0A0A0A] border border-white/10 rounded-xl shadow-2xl max-w-3xl w-full max-h-[88vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="lifestyle-picker-modal"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5">
          <div className="flex items-center gap-2.5">
            <Icons.Users size={15} className="text-amber-400" />
            <div>
              <div className="text-[13px] text-white font-medium">
                Add lifestyle scene
              </div>
              <div className="text-[10px] text-zinc-500">
                Drop AI-generated people into this room — sells the vibe, not
                just the property.
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md text-zinc-500 hover:text-white hover:bg-white/5 transition-colors"
            data-testid="lifestyle-close"
          >
            <Icons.X size={15} />
          </button>
        </div>

        <div className="overflow-y-auto p-5 space-y-5">
          {/* Room chips */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
              Room
            </div>
            <div className="flex flex-wrap gap-2">
              {rooms.map((r) => (
                <button
                  key={r.id}
                  onClick={() => {
                    setActiveRoom(r.id);
                    setActiveScene(r.scenes[0]?.id || null);
                    setCustom("");
                  }}
                  className={`px-3 py-1.5 rounded-full text-[11px] border transition-all ${
                    activeRoom === r.id
                      ? "bg-amber-500/15 border-amber-500/50 text-amber-200"
                      : "bg-black border-white/10 text-zinc-400 hover:border-white/30 hover:text-white"
                  }`}
                  data-testid={`lifestyle-room-${r.id}`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          {/* Scene cards */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
              Scene
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {scenesForRoom.map((s) => (
                <button
                  key={s.id}
                  onClick={() => {
                    setActiveScene(s.id);
                    setCustom("");
                  }}
                  className={`text-left px-3 py-2.5 rounded-lg border transition-all ${
                    activeScene === s.id && !custom.trim()
                      ? "bg-amber-500/10 border-amber-500/40"
                      : "bg-black/40 border-white/5 hover:border-white/20"
                  }`}
                  data-testid={`lifestyle-scene-${s.id}`}
                >
                  <div className="text-[12px] text-white font-medium">
                    {s.label}
                  </div>
                  <div className="text-[10px] text-zinc-500 mt-0.5 leading-snug">
                    {s.blurb}
                  </div>
                </button>
              ))}
              {scenesForRoom.length === 0 && (
                <div className="text-[11px] text-zinc-600 py-4">
                  No catalogue scenes for this room — use custom prompt below.
                </div>
              )}
            </div>
          </div>

          {/* Custom subjects override */}
          <div className="pt-2 border-t border-white/5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
              Or describe your own scene
            </div>
            <textarea
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              placeholder="e.g. an elderly couple having tea at the table by the window, soft afternoon light"
              rows={2}
              className="w-full bg-black border border-white/10 rounded-md px-3 py-2 text-[12px] text-white focus:border-amber-500/50 focus:outline-none resize-none"
              data-testid="lifestyle-custom-input"
            />
            <p className="text-[10px] text-zinc-600 mt-1.5 leading-relaxed">
              Tip: describe 1-3 people, what they're doing, what they're
              wearing in 1-2 short sentences. The room itself will be
              preserved.
            </p>
          </div>
        </div>

        <div className="px-5 py-3 border-t border-white/5 flex items-center justify-between bg-black/30">
          <div className="text-[10px] text-zinc-600">
            Typically 8-15s per scene. Reuse a kitchen photo with 5 different
            scenes for a week of content.
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 rounded-md text-[11px] text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleGo}
              disabled={busy || (!activeScene && !custom.trim())}
              className="px-3.5 py-1.5 rounded-md text-[11px] bg-amber-500/20 border border-amber-500/50 text-amber-200 hover:bg-amber-500/30 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
              data-testid="lifestyle-go"
            >
              {busy ? (
                <>
                  <Icons.Loader2 size={11} className="animate-spin" />
                  Generating…
                </>
              ) : (
                <>
                  <Icons.Sparkles size={11} />
                  Generate scene
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
